import InfoPopover, { PopDot, PopItem, PopLead, PopList, PopSubTitle } from './InfoPopover'
import { DEFAULT_DUE_DAYS, SEVERITY_ORDER, STAGE_ORDER, severityMeta, stageMeta } from './remedialMeta'

const StageHelp = () => (
  <>
    <PopLead>Every remedial moves through these stages. We work the stage out for you from its tasks, so you never have to set it.</PopLead>
    <PopList>
      {STAGE_ORDER.map(stage => {
        const meta = stageMeta(stage)
        return (
          <PopItem key={stage}>
            <PopDot style={{ background: meta.color }} aria-hidden='true' />
            <span><strong>{meta.label}</strong>: {meta.text}</span>
          </PopItem>
        )
      })}
    </PopList>
  </>
)

const OverdueHelp = () => (
  <>
    <PopLead>A remedial is overdue when it is still open and its due date has passed.</PopLead>
    <PopLead>Before it has a task, we use the remedial’s own due date. Once it has tasks, we use the latest deadline among its unfinished tasks, because those tasks now carry the work.</PopLead>
    <PopLead>“Due this week” means due today or in the next 7 days. Remedials awaiting review, resolved or dismissed are never overdue.</PopLead>
    <PopSubTitle>Due dates we set for you</PopSubTitle>
    <PopLead>If nobody picks a date, we set one from the severity. You can change the due date while no task is handling the remedial. Once a task is handling it, the task’s deadline is used instead.</PopLead>
    <PopList>
      {SEVERITY_ORDER.map(level => {
        const meta = severityMeta(level)
        return (
          <PopItem key={level}>
            <PopDot style={{ background: meta.color }} aria-hidden='true' />
            <span><strong>{meta.label}</strong>: {DEFAULT_DUE_DAYS[level]} days after it was raised</span>
          </PopItem>
        )
      })}
    </PopList>
  </>
)

export const StageExplainer = ({ text }) => (
  <InfoPopover label='What does this stage mean?' title='What each stage means' text={text}>
    <StageHelp />
  </InfoPopover>
)

export const OverdueExplainer = ({ text }) => (
  <InfoPopover label='When is a remedial overdue?' title='When a remedial is overdue' text={text}>
    <OverdueHelp />
  </InfoPopover>
)

export const RemedialsHelp = () => (
  <InfoPopover label='Help: what the stages mean and when a remedial is overdue' title='Help with remedials' text='Help'>
    <PopSubTitle>What each stage means</PopSubTitle>
    <StageHelp />
    <PopSubTitle>When a remedial is overdue</PopSubTitle>
    <OverdueHelp />
  </InfoPopover>
)

export const SeverityExplainer = () => (
  <InfoPopover label='What do the severity levels mean?' title='What each severity means'>
    <PopList>
      {SEVERITY_ORDER.map(level => {
        const meta = severityMeta(level)
        return (
          <PopItem key={level}>
            <PopDot style={{ background: meta.color }} aria-hidden='true' />
            <span><strong>{meta.label}</strong>: {meta.text}</span>
          </PopItem>
        )
      })}
    </PopList>
  </InfoPopover>
)

export const RemedialCountsExplainer = ({ withRecent = false }) => (
  <InfoPopover label='What do these remedial numbers mean?' title='What these remedial numbers mean'>
    <PopLead><strong>Open</strong>: remedials still being dealt with, whether or not a task has been created yet. Remedials awaiting review are not counted until someone checks them.</PopLead>
    <PopLead><strong>Overdue</strong>: open remedials whose due date has passed. Once a task is handling a remedial, the task’s deadline is used.</PopLead>
    <PopLead><strong>Needs a decision</strong>: open remedials nobody has acted on yet. Create a task or mark it complete.</PopLead>
    <PopLead><strong>Due this week</strong>: open remedials due today or in the next 7 days.</PopLead>
    <PopLead><strong>Critical</strong>: open remedials rated critical. Each severity is counted on its own.</PopLead>
    {withRecent && <PopLead><strong>Resolved, last 30 days</strong>: remedials put right in the last 30 days, by completing their tasks or by marking them complete.</PopLead>}
    {withRecent && <PopLead><strong>Raised, last 30 days</strong>: remedials raised in the last 30 days, from any source, including ones still awaiting review and ones later dismissed.</PopLead>}
    <PopLead><strong>Which buildings count</strong>: only buildings with task management switched on. Buildings marked as dormant are left out, unless you open that building on its own.</PopLead>
    <PopLead>Click a number to see those remedials.</PopLead>
  </InfoPopover>
)
